import 'twin.macro';
import React from 'react';
import Dialog from '@teetsh/app/src/components/Dialog';
import Button from '@teetsh/app/src/components/Buttons/Button';
import { CheckIcon as Check } from '@heroicons/react/20/solid';
import Body from './Body';
import { atom, useAtom } from 'jotai';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import { useTranslation } from 'react-i18next';
import { useScholarSystem } from 'queries/user/useScholarSystem';
import {
  NiveauValue,
  ScholarSystem,
  ScholarSystems,
  getNiveauFromScholarSystemOrDefault,
} from 'containers/scholarSystem';
import { useSendToCMSProgrammation } from 'containers/Programmations/atoms/useSendToCMSProgrammation';
import { Classroom } from 'types/classroom';
import { useProgDisplayConfig } from 'containers/Programmations/ProgV2/atoms';
import { PrintConfig } from 'containers/Programmations/ProgV2/Preview/types';

interface FormPayload {
  niveau: NiveauValue;
  destProgId: string;
}

export const sendProgToCMSPayloadAtom = atom<
  | {
      programmationId: string;
      programmationName?: string;
      printConfig: PrintConfig;
    }
  | undefined
>(undefined);

function guessNiveau(
  scholarSystem: ScholarSystem,
  classrooms: Classroom[],
  programmationName?: string,
): NiveauValue {
  const title = programmationName
    ?.replaceAll(/Petite Section/gi, 'PS')
    ?.replaceAll(/Moyenne Section/gi, 'MS')
    ?.replaceAll(/Grande Section/gi, 'GS');
  const defaultNiveauFromTitle = scholarSystem.niveaux.find(
    n =>
      title?.startsWith(`${n.niveau} `) ||
      title?.endsWith(` ${n.niveau}`) ||
      title?.includes(` ${n.niveau} `),
  )?.niveau;
  const defaultNiveauFromClassroom = getNiveauFromScholarSystemOrDefault(
    scholarSystem,
    classrooms[0]?.degree,
  )?.niveau;

  return (
    defaultNiveauFromTitle ||
    defaultNiveauFromClassroom ||
    scholarSystem.defaultNiveau
  );
}

export default function ProgSendToCMSModal() {
  const { sendToCMS } = useSendToCMSProgrammation();
  const allClassrooms = useClassrooms();
  const { scholarSystem = ScholarSystems.FR } = useScholarSystem();
  const { t } = useTranslation(['common', 'programmation']);
  const [modalPayload, setModalPayload] = useAtom(sendProgToCMSPayloadAtom);
  const onClose = () => setModalPayload(undefined);
  const displayConfig = useProgDisplayConfig();

  if (!modalPayload) {
    return null;
  }

  return (
    <Dialog
      title={t('programmation:sendToCMSModalTitle')}
      isOpen={!!modalPayload}
      onClose={onClose}
      size="lg"
    >
      <Form<FormPayload>
        keepDirtyOnReinitialize
        initialValues={{
          niveau: guessNiveau(
            scholarSystem,
            allClassrooms,
            modalPayload.programmationName,
          ),
          destProgId: modalPayload.programmationId,
        }}
        onSubmit={values =>
          sendToCMS({
            programmationId: modalPayload?.programmationId,
            grade: values.niveau,
            destProgId: values.destProgId,
            displayConfig,
            printConfig: modalPayload.printConfig,
          })
            .then(() => setModalPayload(undefined))
            .catch(() => ({
              [FORM_ERROR]: t('common:messages.genericError'),
            }))
        }
      >
        {({ handleSubmit, submitting, submitError }) => (
          <form onSubmit={handleSubmit}>
            <Dialog.Body tw="min-h-72">
              <Body niveaux={scholarSystem.niveaux.map(n => n.niveau)} />
              {submitError && (
                <div tw="mt-8 text-right text-danger-moderate mb-3 font-medium">
                  {submitError}
                </div>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button minimal large type="button" onClick={onClose}>
                {t('common:buttons.cancel')}
              </Button>
              <Button
                type="submit"
                loading={submitting}
                large
                intent="primary"
                icon={Check}
              >
                {t('common:buttons.validate')}
              </Button>
            </Dialog.Footer>
          </form>
        )}
      </Form>
    </Dialog>
  );
}
